import { BankAccountWithDetails, CryptoCounterparty, CryptoTransfer } from '@column/api';

import { TimelineEntry, TimelinePartyType, TimelineStatus } from '../_components/Timeline';
import { TransferParty } from '../_components/Timeline/types';

// External crypto transfer states, per Column docs (docs.column.com/crypto/notifications-and-states):
//   incoming: detected -> initiated -> manual_review -> completed | returned | removed
//   outgoing: initiated -> manual_review -> completed | returned
type StablecoinEventDef = {
  label: string;
  field: keyof CryptoTransfer;
  status?: TimelineStatus;
};

// created_at anchors the initial state (detection for incoming, initiation for outgoing).
const incomingEvents: StablecoinEventDef[] = [
  { label: 'Detected', field: 'created_at', status: TimelineStatus.InProgress },
  { label: 'Initiated', field: 'initiated_at', status: TimelineStatus.InProgress },
  { label: 'Manual Review', field: 'manual_review_at', status: TimelineStatus.Warning },
  { label: 'Completed', field: 'completed_at', status: TimelineStatus.Success },
  { label: 'Removed', field: 'removed_at', status: TimelineStatus.Danger },
];

const outgoingEvents: StablecoinEventDef[] = [
  { label: 'Initiated', field: 'created_at', status: TimelineStatus.InProgress },
  { label: 'Manual Review', field: 'manual_review_at', status: TimelineStatus.Warning },
  { label: 'Completed', field: 'completed_at', status: TimelineStatus.Success },
];

// Only Ethereum and Base are publicly supported today. Solana is intentionally omitted until enabled.
const STABLECOIN_EXPLORERS: Record<string, { txBaseUrl: string; name: string }> = {
  ethereum: { txBaseUrl: 'https://etherscan.io/tx/', name: 'Etherscan' },
  base: { txBaseUrl: 'https://basescan.org/tx/', name: 'BaseScan' },
};

export const getStablecoinExplorerTxUrl = (network?: string, transactionHash?: string): string | undefined => {
  if (!network || !transactionHash) {
    return undefined;
  }
  const explorer = STABLECOIN_EXPLORERS[network.toLowerCase()];
  return explorer ? `${explorer.txBaseUrl}${transactionHash}` : undefined;
};

// Falls back to a generic label when the network is unknown.
export const getStablecoinExplorerName = (network?: string): string => {
  const explorer = network ? STABLECOIN_EXPLORERS[network.toLowerCase()] : undefined;
  return explorer?.name ?? 'Block Explorer';
};

export const cryptoCounterpartyName = (counterparty: CryptoCounterparty): string =>
  counterparty.type === 'business'
    ? (counterparty.business_name ?? 'Unnamed Counterparty')
    : [counterparty.first_name, counterparty.last_name].filter(Boolean).join(' ') || 'Unnamed Counterparty';

// Direction decides who sends/receives and which on-chain address belongs to which party. The
// counterparty's own record is authoritative for its wallet/network when we have it.
const buildStablecoinParties = (
  transfer: CryptoTransfer,
  counterparty?: CryptoCounterparty,
  bankAccount?: BankAccountWithDetails
): { senderParty: TransferParty; receiverParty: TransferParty } => {
  const columnWallet = transfer.is_incoming ? transfer.recipient_address : transfer.sender_address;
  const counterpartyWallet =
    counterparty?.wallet_address ?? (transfer.is_incoming ? transfer.sender_address : transfer.recipient_address);
  const counterpartyType =
    counterparty?.type === 'business' ? 'business' : counterparty?.type === 'person' ? 'person' : undefined;

  const paymentAddressParty: TransferParty = {
    partyType: transfer.is_incoming ? TimelinePartyType.Receiver : TimelinePartyType.Sender,
    data: {
      cryptoRole: 'payment_address',
      name: bankAccount?.display_name || bankAccount?.description || 'Payment Address',
      walletAddress: columnWallet,
      network: transfer.network,
      asset: transfer.asset,
      bankAccountId: transfer.bank_account_id,
    },
  };

  const counterpartyParty: TransferParty = {
    partyType: transfer.is_incoming ? TimelinePartyType.Sender : TimelinePartyType.Receiver,
    data: {
      cryptoRole: 'counterparty',
      name: counterparty ? cryptoCounterpartyName(counterparty) : 'Counterparty',
      walletAddress: counterpartyWallet,
      network: counterparty?.chain ?? transfer.network,
      counterpartyId: transfer.crypto_counterparty_id,
      counterpartyType,
    },
  };

  return transfer.is_incoming
    ? { senderParty: counterpartyParty, receiverParty: paymentAddressParty }
    : { senderParty: paymentAddressParty, receiverParty: counterpartyParty };
};

export const buildStablecoinEvents = ({
  transfer,
  counterparty,
  bankAccount,
}: {
  transfer: CryptoTransfer;
  counterparty?: CryptoCounterparty;
  bankAccount?: BankAccountWithDetails;
}): TimelineEntry[] => {
  const events = transfer.is_incoming ? incomingEvents : outgoingEvents;
  const isReturned = !!transfer.returned_at || !!transfer.return_reason;
  const isRemoved = !!transfer.removed_at;

  const { senderParty, receiverParty } = buildStablecoinParties(transfer, counterparty, bankAccount);

  // The fiat amount is debited in USD from the payment address's bank account (stored in cents).
  const fiatAmount = transfer.amount || undefined;

  const entries: TimelineEntry[] = events
    .filter(
      (event) =>
        event.field === 'created_at' ||
        (event.field === 'completed_at' && !isReturned && !isRemoved) ||
        !!transfer[event.field]
    )
    .map((event) => {
      const raw = transfer[event.field] as string | undefined;
      // Sender anchors the opening event; receiver anchors the terminal event (completion, or removal).
      const partyData =
        event.field === 'created_at'
          ? senderParty
          : event.field === 'completed_at' || event.field === 'removed_at'
            ? receiverParty
            : undefined;
      // Opening event debits the sender, completion credits the receiver; isAdding drives the sign.
      const amount =
        fiatAmount !== undefined && (event.field === 'created_at' || event.field === 'completed_at')
          ? { value: fiatAmount, currencyCode: 'USD', isAdding: event.field === 'completed_at' }
          : undefined;
      return {
        label: event.label,
        date: raw ? new Date(raw) : undefined,
        status: raw ? event.status : TimelineStatus.Pending,
        partyData,
        amount,
      };
    });

  if (isReturned) {
    entries.push({
      label: 'Returned',
      status: TimelineStatus.Returned,
      date: transfer.returned_at ? new Date(transfer.returned_at) : undefined,
      returnData: transfer.return_reason ? { code: '', reason: transfer.return_reason, description: '' } : undefined,
      // Completed is filtered out for returned transfers, so surface the receiver here unless it already completed.
      partyData: transfer.completed_at ? undefined : receiverParty,
    });
  }

  return entries;
};
